import { formatDistanceToNowStrict } from "date-fns";

import {
  ActionMenu,
  type MenuItem,
} from "@vega-core/components/ActionMenu/ActionMenu";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { parseUtcDate } from "@vega-core/utils/formatDate";

import { useNotificationCenter } from "../NotificationCenter.context";
import { type NotificationCenterItem as NotificationItem } from "../NotificationCenter.types";

type Props = {
  notification: NotificationItem;
};

const formatRelativeTime = (createdAt: string): string | null => {
  const date = parseUtcDate(createdAt);

  if (Number.isNaN(date.getTime())) return null;

  return formatDistanceToNowStrict(date, { addSuffix: true });
};

export const NotificationCenterItem = ({ notification }: Props) => {
  const { markRead, markUnread, dismiss, onItemClick } =
    useNotificationCenter();
  const isUnread = !notification.readAt;
  const relativeTime = formatRelativeTime(notification.createdAt);

  const handleClick = () => {
    if (isUnread) markRead(notification.id);

    onItemClick?.(notification);
  };

  const menuItems: MenuItem[] = [];

  if (isUnread) {
    menuItems.push({
      title: m.notificationCenter_markAsRead(),
      onClick: () => markRead(notification.id),
    });
  } else if (markUnread) {
    menuItems.push({
      title: m.notificationCenter_markAsUnread(),
      onClick: () => markUnread(notification.id),
    });
  }

  if (dismiss) {
    menuItems.push({
      title: m.notificationCenter_dismiss(),
      onClick: () => dismiss(notification.id),
    });
  }

  return (
    <div className="flex items-start hover:bg-background/secondary defaultTransition">
      <button
        aria-label={
          isUnread
            ? `${notification.title} ${m.notificationCenter_unreadSuffix()}`
            : notification.title
        }
        className="flex-1 min-w-0 text-left flex items-start gap-3 px-4 py-3 outline-none focus:outline-none focus-visible:bg-background/secondary"
        data-testid="notification-center-item"
        data-unread={isUnread ? "true" : undefined}
        onClick={handleClick}
        type="button"
      >
        {notification.icon ? (
          <span
            aria-hidden="true"
            className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-default bg-background/tertiary text-text/secondary"
          >
            {notification.icon}
          </span>
        ) : null}
        <div className="flex-1 min-w-0 flex flex-col">
          <p
            className={cnTwMerge(
              "body-small-medium text-text/primary",
              isUnread && "body-small-semibold",
            )}
          >
            {notification.title}
          </p>
          {notification.body ? (
            <p className="body-small-regular text-text/secondary line-clamp-3">
              {notification.body}
            </p>
          ) : null}
          {relativeTime ? (
            <p className="mt-1 inline-flex items-center gap-1.5 body-extra-small-regular text-text/secondary">
              {isUnread ? (
                <span
                  aria-hidden="true"
                  className="shrink-0 h-1.5 w-1.5 rounded-full bg-brand-default"
                />
              ) : null}
              {relativeTime}
            </p>
          ) : null}
        </div>
      </button>
      {menuItems.length > 0 ? (
        <div className="shrink-0 pr-2 pt-2">
          <ActionMenu
            compact
            contentClassName="z-tooltip"
            dataTestId="notification-center-item-actions"
            menuItems={menuItems}
            variant="tertiary"
            withBorders={false}
          />
        </div>
      ) : null}
    </div>
  );
};
